import React, { useCallback, useState, useContext } from 'react'
import { Modal } from 'react-bootstrap'
// import { connect } from 'react-redux'
import { MainModal } from '../styles/utils'

import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import { v4 as uuidv4 } from 'uuid'
import { useDropzone } from 'react-dropzone'
import authorization from 'live/services/authorization'
import ActionToggle from 'live/components/ui/action-toggle'

import * as S from '../styles/attachmentsStyle'
import moment from 'moment'
import globalNotification from 'live/services/global-notification'
import { getFiles } from '../api/getFiles'
import UploadButton from './UploadButton2'
import { File, AttachmentsListInterface } from '../interface'
import { PermissionsAPI, checkPermission, getDefaultPropIsPublic } from '../api/permissions'
import { PermissionsContext } from './Uploads'

const AttachmentsModal = ({
    showModal,
    setShowModal,
    setFilesList,
    id,
    attachments
}: {
    showModal: boolean
    setShowModal: (files: boolean) => void
    setFilesList: (files: File[]) => void
    id: string | null
    attachments: AttachmentsListInterface
}): JSX.Element => {
    const [selected, setSelected] = useState(null)
    const [userInfo, setUserInfo] = useState({
        username: authorization.user.username || '--'
    })
    const [files, setFiles] = useState([])
    const [errorMsg, setErrorMsg] = useState(null)
    const attachmentsList = attachments

    const permissions = useContext(PermissionsContext)
    const fetchApi = (url, method, body) => {
        return fetch(url, {
            method,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(body)
        })
    }

    const addFiles = files => {
        setFiles(oldFiles => {
            const size = 0
            const filesToAdd = files.reduce((total, file) => {
                // if (file.size <= UPLOAD_SIZE_LIMIT) total.push(file);
                total.push(file)

                return total
            }, [])

            if (filesToAdd.length !== files.length) {
                setErrorMsg('Some files were not uploaded because they exceed the individual size limit of 20MB.')
            } else {
                setErrorMsg(null)
            }

            let origin = ''
            if (!window.location.origin) {
                origin = window.location.protocol + '//' + window.location.hostname
            } else {
                origin = window.location.origin
            }
            return [
                ...oldFiles,
                ...filesToAdd.reduce(
                    (prev, cur) => [
                        ...prev,
                        {
                            ref: cur,
                            origin,
                            uuid: uuidv4(),
                            timestamp: Date.now(),
                            size: cur.size,
                            lastModified: cur?.lastModified,
                            isPublic: getDefaultPropIsPublic(permissions)
                        }
                    ],
                    []
                )
            ]
        })
    }

    const onDrop = useCallback(file => addFiles(file), [])
    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
        onDrop,
        noClick: true
    })
    const labels = ['title', 'type', 'date added', 'public', '']

    const removeFile = fileUuid => {
        setFiles(oldFiles => {
            const index = oldFiles.findIndex(file => file.uuid === fileUuid)
            if (index !== -1) return [...oldFiles.slice(0, index), ...oldFiles.slice(index + 1)]

            return oldFiles
        })
    }

    const resetAttachmentState = persist => {
        setShowModal(false)
        setErrorMsg(null)
        if (!persist) {
            setFiles([])
        }
    }

    const getAttachmentFromUuid = uuid => {
        return fetch(`/services/plugin-upload/info/${uuid}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            credentials: 'include'
        }).then(response => response.json())
    }

    const sendFile = (uploads, username) => {
        const promises = []
        uploads.forEach(upload => {
            const formData = new FormData()
            formData.append('file', upload.ref)
            formData.append('name', upload.ref.name)
            formData.append('type', upload.ref.type)
            formData.append('username', username)
            formData.append('size', upload.size)
            formData.append('lastModified', upload?.lastModified)
            formData.append('isPublic', upload.isPublic)

            promises.push(
                fetch('/services/plugin-upload/upload', {
                    method: 'POST',
                    headers: { Accept: 'application/json' },
                    credentials: 'include',
                    body: formData
                }).then(response => {
                    if (response.ok) {
                        return response.json().then(body => getAttachmentFromUuid(body.uuid))
                    }
                })
            )
        })

        return Promise.all(promises).then(responses => {
            const msgs = []
            responses.forEach(response => msgs.push(response))

            return Promise.all(msgs)
        })
    }

    const submit = () => {
        if (files.length == 0) {
            setErrorMsg(`you need to upload at least one file before send it`)
        } else {
            sendFile(files, userInfo.username).then(attachments => {
                attachments = attachments.map(attachment => {
                    return {
                        uploadUserName: attachment.username,
                        fileUuid: attachment.uuid,
                        fileName: attachment.name,
                        fileType: attachment.type,
                        size: attachment.size,
                        lastModified: attachment?.lastModified,
                        uploadTimestamp: parseInt(attachment.timestamp),
                        id: id,
                        isPublic: attachment.isPublic
                    }
                })
                // saveNewAttachments([...(attachmentsList.defaultValue || []), ...attachments], id)
                resetAttachmentState(false)
                getFiles(setFilesList)
            })
        }
    }

    return (
        <>
            <UploadButton setShowModal={setShowModal} />
            <MainModal
                backdrop={false}
                style={{ backgroundColor: `rgba(0, 0, 0, ${showModal ? 0.3 : 0})` }}
                show={showModal}
                onHide={() => null}
                data-html2canvas-ignore="true"
            >
                <Modal.Header>
                    <h3 className="modal-title">Upload Attachments</h3>
                </Modal.Header>
                <Modal.Body>
                    <S.Attachments>
                        <S.Toolbar>
                            <button className={`btn btn-primary btn-sm`} onClick={open}>
                                <input {...getInputProps()} />
                                <i className="fa fa-upload" />
                                Upload a File
                            </button>
                            <span className="text-danger">{errorMsg}</span>
                        </S.Toolbar>
                        <S.UploadZone
                            className={isDragActive ? 'active' : ''}
                            {...getRootProps({ refKey: 'innerRef' })}
                        >
                            <i className="fal fa-parachute-box fa-7x animation-rotate" />
                            <span>Drop files here</span>
                        </S.UploadZone>
                        <S.Column>
                            <S.UploadsPreview className="labels">
                                {labels.map(label => (
                                    <div key={`field_${label}`}>{label}</div>
                                ))}
                            </S.UploadsPreview>

                            <S.ListContainer>
                                {files.map((file, i) => (
                                    <FileEntry
                                        key={`attachment_${i}`}
                                        file={file}
                                        files={files}
                                        setFiles={setFiles}
                                        removeFile={removeFile}
                                    />
                                ))}
                            </S.ListContainer>
                        </S.Column>
                        <S.Footnote>You can drag and drop files too</S.Footnote>
                    </S.Attachments>
                </Modal.Body>
                <Modal.Footer>
                    <SubmitOrCancel
                        disabled={files.length == 0}
                        className=""
                        onCancel={() => resetAttachmentState(false)}
                        onSubmit={submit}
                        type="button"
                    />
                </Modal.Footer>
            </MainModal>
        </>
    )
}

const FileEntry = ({ file, removeFile, files, setFiles }) => {
    const handleIsPublic = file => {
        setFiles(oldFiles => {
            const objIndex = oldFiles.indexOf(file)
            if (objIndex !== -1) {
                let object = oldFiles[objIndex]
                object.isPublic = !object.isPublic

                return [...oldFiles.slice(0, objIndex), object, ...oldFiles.slice(objIndex + 1)]
            }

            return oldFiles
        })
    }
    const permissions = useContext(PermissionsContext)
    const editStatusFile = permissions.uploadPrivateFile && permissions.uploadPublicFile

    return (
        file &&
        file.ref && (
            <S.UploadsPreview>
                <div>
                    <div>
                        <i className={`far fa-file-upload`} />
                    </div>
                    <span>
                        {`${file.ref.name.substr(0, 22)}`}
                        {file.ref.name.length > 22 ? '...' : ''}
                    </span>
                </div>
                <div>
                    <span>{file.ref.type}</span>
                </div>
                <div>
                    <span>{moment(file.timestamp).format('MMMM Do YYYY, h:mm A')}</span>
                </div>
                <div>
                    <ActionToggle
                        active={Boolean(file.isPublic)}
                        disabled={!editStatusFile}
                        onChange={() => handleIsPublic(file)}
                    />
                </div>
                <div>
                    <i className="far fa-trash-alt" onClick={() => removeFile(file.uuid)} />
                </div>
            </S.UploadsPreview>
        )
    )
}

export default AttachmentsModal
